import { useQuery } from "@tanstack/react-query";
import {
    CheckCircleIcon,
    ClockIcon,
    EnvelopeIcon,
    ExclamationTriangleIcon,
    LockClosedIcon,
    PhoneIcon,
    ShieldCheckIcon,
    UserCircleIcon,
} from "@heroicons/react/24/outline";
import { useMemo } from "react";
import { Link } from "react-router-dom";

import { Button } from "@/components/ui/Button";
import { PermissionSummary } from "@/features/admin-management/ui/PermissionCatalogEditor";
import { useAuth } from "@/features/auth/state/useAuth";
import { extractApiError } from "@/lib/api-error";

import { getCurrentAdminProfile } from "../api/settingsApi";

const formatDate = (value: string | null | undefined) => {
    if (!value) return "—";
    return new Date(value).toLocaleString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
};

const InfoItem = ({
    icon: Icon,
    label,
    value,
}: {
    icon: React.ElementType;
    label: string;
    value: React.ReactNode;
}) => (
    <div className="flex gap-3.5">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400">
            <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0 pt-0.5">
            <p className="text-xs font-medium text-slate-400 dark:text-slate-500">{label}</p>
            <div className="mt-1 break-words text-sm font-semibold text-slate-900 dark:text-white">
                {value}
            </div>
        </div>
    </div>
);

const StatusBadge = ({ status }: { status: string }) => {
    const isActive = status === "ACTIVE";

    return (
        <span
            className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${
                isActive
                    ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400"
                    : "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"
            }`}
        >
            <span className="h-1.5 w-1.5 rounded-full bg-current" />
            {status}
        </span>
    );
};

const roleLabel = (role: string | null | undefined) =>
    role
        ? role
              .toLowerCase()
              .replace(/_/g, " ")
              .replace(/\b\w/g, (character) => character.toUpperCase())
        : "—";

export const SettingsRoute = () => {
    const { user } = useAuth();
    const profileQuery = useQuery({
        queryKey: ["current-admin-profile"],
        queryFn: getCurrentAdminProfile,
    });

    const profile = profileQuery.data?.data;
    const displayRole = useMemo(
        () => roleLabel(profile?.role || user?.role),
        [profile?.role, user?.role]
    );

    if (profileQuery.isLoading && !profile) {
        return (
            <div className="space-y-5">
                <div className="h-44 animate-pulse rounded-3xl bg-slate-200 dark:bg-slate-800" />
                <div className="grid gap-5 md:grid-cols-2">
                    <div className="h-56 animate-pulse rounded-2xl bg-slate-200 dark:bg-slate-800" />
                    <div className="h-56 animate-pulse rounded-2xl bg-slate-200 dark:bg-slate-800" />
                </div>
            </div>
        );
    }

    if ((profileQuery.error || !profile) && !profileQuery.isLoading) {
        return (
            <div className="rounded-2xl border border-rose-200 bg-rose-50 p-8 text-center dark:border-rose-800 dark:bg-rose-900/20">
                <ExclamationTriangleIcon className="mx-auto h-10 w-10 text-rose-400" />
                <h2 className="mt-3 text-base font-semibold text-slate-900 dark:text-white">
                    We couldn&apos;t load your profile
                </h2>
                <p className="mt-1 text-sm text-rose-600 dark:text-rose-400">
                    {extractApiError(profileQuery.error)}
                </p>
                <Button type="button" variant="outline" onClick={() => profileQuery.refetch()} className="mt-5">
                    Try again
                </Button>
            </div>
        );
    }

    const profileData = profile as NonNullable<typeof profile>;
    const initials = `${profileData.firstName?.[0] ?? ""}${profileData.lastName?.[0] ?? ""}`.toUpperCase() || "AD";

    return (
        <div className="space-y-5">
            <section className="relative overflow-hidden rounded-3xl bg-slate-950 p-6 text-white shadow-xl shadow-slate-900/10 sm:p-8">
                <div className="absolute -right-20 -top-24 h-64 w-64 rounded-full bg-primary-600/30 blur-3xl" />
                <div className="absolute -bottom-32 left-1/3 h-56 w-56 rounded-full bg-accent-500/20 blur-3xl" />
                <div className="relative flex flex-col justify-between gap-7 sm:flex-row sm:items-center">
                    <div className="flex items-center gap-4">
                        <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-primary-500 text-lg font-bold shadow-lg shadow-black/20">
                            {initials}
                        </div>
                        <div>
                            <p className="text-xl font-semibold tracking-tight">
                                {profileData.fullName || `${profileData.firstName} ${profileData.lastName}`}
                            </p>
                            <p className="mt-1 text-sm text-slate-300">{profileData.email}</p>
                            <div className="mt-3 flex flex-wrap items-center gap-2">
                                <span className="rounded-full bg-white/10 px-2.5 py-1 text-xs font-semibold text-slate-100">
                                    {displayRole}
                                </span>
                                <StatusBadge status={profileData.status} />
                            </div>
                        </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-5 border-t border-white/10 pt-5 text-sm sm:border-l sm:border-t-0 sm:pl-8 sm:pt-0">
                        <div className="grid grid-cols-2 gap-x-8 gap-y-3">
                            <div>
                                <p className="text-xs text-slate-400">Email verification</p>
                                <p className="mt-1 flex items-center gap-1.5 font-medium">
                                    <CheckCircleIcon className="h-4 w-4 text-emerald-400" />
                                    {profileData.emailVerified ? "Verified" : "Pending"}
                                </p>
                            </div>
                            <div>
                                <p className="text-xs text-slate-400">Account since</p>
                                <p className="mt-1 font-medium">{formatDate(profileData.createdAt).split(" at ")[0]}</p>
                            </div>
                        </div>
                        <Link
                            to="/settings/security"
                            className="inline-flex items-center gap-2 rounded-xl bg-white/10 px-3 py-2 text-xs font-semibold text-white transition hover:bg-white/20"
                        >
                            <LockClosedIcon className="h-4 w-4" />
                            Security
                        </Link>
                    </div>
                </div>
            </section>

            <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900 sm:p-6">
                <div className="mb-5 flex items-start justify-between gap-4">
                    <div>
                        <h3 className="text-base font-semibold text-slate-900 dark:text-white">Personal information</h3>
                        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Information associated with your admin account.</p>
                    </div>
                    <UserCircleIcon className="h-6 w-6 text-slate-300 dark:text-slate-600" />
                </div>
                <div className="grid gap-x-8 gap-y-6 border-t border-slate-100 pt-5 dark:border-slate-800 sm:grid-cols-2">
                    <InfoItem
                        icon={UserCircleIcon}
                        label="Full name"
                        value={profileData.fullName || "—"}
                    />
                    <InfoItem
                        icon={EnvelopeIcon}
                        label="Email address"
                        value={
                            <span className="inline-flex items-center gap-1.5">
                                {profileData.email}
                                {profileData.emailVerified ? <CheckCircleIcon className="h-4 w-4 text-emerald-500" /> : null}
                            </span>
                        }
                    />
                    <InfoItem icon={PhoneIcon} label="Phone number" value={profileData.phoneNumber || "—"} />
                    <InfoItem icon={ShieldCheckIcon} label="Account role" value={displayRole} />
                    <InfoItem
                        icon={ClockIcon}
                        label="Last updated"
                        value={formatDate(profileData.updatedAt)}
                    />
                </div>
            </section>

            <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900 sm:p-6">
                <div className="flex flex-col justify-between gap-3 border-b border-slate-100 pb-5 sm:flex-row sm:items-start dark:border-slate-800">
                    <div>
                        <h3 className="text-base font-semibold text-slate-900 dark:text-white">Access & permissions</h3>
                        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                            Review the permissions assigned directly to you and the access currently in effect.
                        </p>
                    </div>
                    <div className="flex shrink-0 items-center gap-2 rounded-xl bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400">
                        <ShieldCheckIcon className="h-4 w-4" />
                        {(profileData.effectivePermissions ?? []).length} active permissions
                    </div>
                </div>
                <div className="grid gap-6 pt-5 xl:grid-cols-2">
                    <div>
                        <p className="mb-3 text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">Explicit access</p>
                        <PermissionSummary
                            permissions={profileData.permissions}
                            emptyLabel="No explicit permissions assigned"
                            maxVisible={8}
                        />
                    </div>
                    <div>
                        <p className="mb-3 text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">Effective access</p>
                        <PermissionSummary
                            permissions={profileData.effectivePermissions}
                            emptyLabel="No effective permissions returned"
                            maxVisible={8}
                        />
                    </div>
                </div>
            </section>
        </div>
    );
};
